import { useCallback, useEffect, useRef } from "react";

import { registerDismissable } from "./dismissableRegistry";

// Process-wide so an inner dialog's close can't unlock scroll under an outer one.
let scrollLockCount = 0;
let scrollLockRestore: string | null = null;

function lockPageScroll(): void {
  if (scrollLockCount === 0) {
    scrollLockRestore = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
  }
  scrollLockCount += 1;
}

function unlockPageScroll(): void {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.documentElement.style.overflow = scrollLockRestore ?? "";
    scrollLockRestore = null;
  }
}

export interface UseDialogOptions {
  open: boolean;
  /** Called once per user-driven close (Escape, backdrop, form close). */
  onClose: () => void;
  /** When false, Escape and backdrop clicks are ignored. */
  dismissable?: boolean;
}

export interface UseDialogResult {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  handleCancel: (event: React.SyntheticEvent<HTMLDialogElement>) => void;
  handleClose: (event: React.SyntheticEvent<HTMLDialogElement>) => void;
}

/**
 * Drives a native modal `<dialog>` from React state. `handleCancel`/`handleClose`
 * are optional: the hook also listens natively and reports each close once.
 */
export function useDialog({
  open,
  onClose,
  dismissable = true,
}: UseDialogOptions): UseDialogResult {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Registered even when not dismissable: it still swallows Escape.
  useEffect(() => {
    if (!open) return undefined;
    return registerDismissable();
  }, [open]);

  const onCloseRef = useRef(onClose);
  const dismissableRef = useRef(dismissable);
  useEffect(() => {
    onCloseRef.current = onClose;
    dismissableRef.current = dismissable;
  });

  // One native `close` can arrive via our listener and a wired handleClose.
  const handledEventRef = useRef<Event | null>(null);
  // Our own close (open went false) must not bounce back; `close` is task-queued, hence a ref.
  const suppressNextCloseRef = useRef(false);

  // React re-dispatches close/cancel up the tree, so a nested dialog's close would reach its host.
  const ownEvent = useCallback(
    (nativeEvent: Event) => nativeEvent.target === dialogRef.current,
    [],
  );

  const runClose = useCallback(
    (nativeEvent: Event) => {
      if (!ownEvent(nativeEvent)) return;
      if (handledEventRef.current === nativeEvent) return;
      handledEventRef.current = nativeEvent;
      if (suppressNextCloseRef.current) {
        suppressNextCloseRef.current = false;
        return;
      }
      onCloseRef.current();
    },
    [ownEvent],
  );

  const runCancel = useCallback(
    (nativeEvent: Event) => {
      if (!ownEvent(nativeEvent)) return;
      if (!dismissableRef.current) nativeEvent.preventDefault();
    },
    [ownEvent],
  );

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) =>
      runCancel(event.nativeEvent),
    [runCancel],
  );

  const handleClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) =>
      runClose(event.nativeEvent),
    [runClose],
  );

  // `open` dep: the <dialog> may only be rendered while open.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeCancel = (event: Event) => runCancel(event);
    const handleNativeClose = (event: Event) => runClose(event);

    // Backdrop presses target the dialog itself; pointerdown so a drag ending outside doesn't dismiss.
    const handlePointerDown = (event: PointerEvent) => {
      if (!dismissableRef.current || event.button !== 0) return;
      if (event.target !== dialog) return;
      const content = dialog.firstElementChild ?? dialog;
      const box = content.getBoundingClientRect();
      const outside =
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom;
      if (outside) dialog.close();
    };

    dialog.addEventListener("cancel", handleNativeCancel);
    dialog.addEventListener("close", handleNativeClose);
    dialog.addEventListener("pointerdown", handlePointerDown);
    return () => {
      dialog.removeEventListener("cancel", handleNativeCancel);
      dialog.removeEventListener("close", handleNativeClose);
      dialog.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, runCancel, runClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open && dialog.isConnected) {
        dialog.showModal();
        // showModal would focus the close button with a visible ring after a keypress; park focus on the dialog.
        if (!dialog.querySelector("[autofocus]")) {
          dialog.tabIndex = -1;
          dialog.focus();
        }
      }
      lockPageScroll();
      return () => unlockPageScroll();
    }

    if (dialog.open) {
      suppressNextCloseRef.current = true;
      dialog.close();
    }
    return undefined;
  }, [open]);

  return { dialogRef, handleCancel, handleClose };
}
